import { differenceInCalendarDays } from "date-fns";
import { DateTime } from "luxon";
import { Label } from "north.js";
import type { ComponentProps } from "react";

import {
  SWAG_BAG_ARRIVAL_DEADLINE,
  GENERAL_EVENT_SWAG_ARRIVAL_DEADLINE,
  SHIPMENT_STAGE_LABELS,
} from "./constants";
import { ShipmentStage, Shipment, ShipmentsData } from "./types";

type LabelColor = ComponentProps<typeof Label>["color"];

const EMPTY_SHIPMENTS_DATA: ShipmentsData = {
  version: 1,
  shipments: [],
};

export const emptyShipment: Shipment = {
  description: "",
  tracking_codes: [],
  shipping_receipt_file_id: [],
  total_boxes: 0,
  date_shipped: "",
  eta: "",
  sponsor_shipping_notes: "",
  organizer_shipping_notes: "",
  include_in_swag_bags: false,
  arrival_date: "",
  boxes_received: 0,
  stage: "draft",
};

export const createEmptyShipment = (): Shipment => ({
  ...emptyShipment,
  tracking_codes: [],
  shipping_receipt_file_id: [],
});

export const REQUIRED_SHIPMENT_FIELDS = [
  "description",
  "eta",
  "date_shipped",
] as const;

export const parseShipmentsData = (
  raw: string | null | undefined
): ShipmentsData => {
  if (!raw) return EMPTY_SHIPMENTS_DATA;

  try {
    const parsed = JSON.parse(raw) as ShipmentsData;
    if (parsed?.version >= 1 && Array.isArray(parsed.shipments)) {
      return parsed;
    }
  } catch {
    return EMPTY_SHIPMENTS_DATA;
  }

  return EMPTY_SHIPMENTS_DATA;
};

export const formatShipmentDate = (
  value: string | null | undefined
): string => {
  if (!value) return "—";

  const date = DateTime.fromISO(value);
  if (!date.isValid) return value;

  return date.toLocaleString({
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const getShipmentDeadline = (includeInSwagBags: boolean): string =>
  includeInSwagBags
    ? SWAG_BAG_ARRIVAL_DEADLINE
    : GENERAL_EVENT_SWAG_ARRIVAL_DEADLINE;

// returns null when the shipment isn't late
export const getShipmentLateWarning = (shipment: Shipment): string | null => {
  const deadline = getShipmentDeadline(shipment.include_in_swag_bags);
  const deadlineLabel = shipment.include_in_swag_bags
    ? "swag bag packing"
    : "event arrival";

  const arrivalDate = shipment.arrival_date || shipment.eta;
  if (!arrivalDate) return null;

  const parsedArrival = new Date(arrivalDate);
  if (isNaN(parsedArrival.getTime())) return null;

  const daysOver = differenceInCalendarDays(parsedArrival, new Date(deadline));
  if (daysOver <= 0) return null;

  const verb = shipment.arrival_date ? "arrived" : "is estimated to arrive";
  return `This shipment ${verb} on ${formatShipmentDate(
    arrivalDate
  )}, which misses the ${deadlineLabel} deadline of ${formatShipmentDate(
    deadline
  )}.`;
};

export const getArrivalDateColor = (
  arrivalDate: string | null | undefined,
  eta: string | null | undefined,
  includeInSwagBags = false
): LabelColor => {
  const deadline = getShipmentDeadline(includeInSwagBags);

  const dateToEvaluate = arrivalDate || eta;
  if (!dateToEvaluate) return "secondary";

  const parsedDate = new Date(dateToEvaluate);
  if (isNaN(parsedDate.getTime())) return "secondary";

  const daysFromDeadline = differenceInCalendarDays(
    parsedDate,
    new Date(deadline)
  );

  if (daysFromDeadline <= 0) return "success";
  return "danger";
};

export const getShipmentStageLabel = (stage: ShipmentStage): string =>
  SHIPMENT_STAGE_LABELS[stage] ?? stage;

export const shipmentsEqual = (a: Shipment, b: Shipment): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

export const formatShipmentSlackMessage = (
  driName: string,
  companyName: string,
  shipment: Shipment,
  isNew: boolean
): string => {
  const action = isNew ? "shipped" : "updated";

  return [
    `(${driName}) *${companyName}* ${action} a swag shipment:`,
    "",
    `*Description:* ${shipment.description || "—"}`,
    `*Include in swag bags:* ${shipment.include_in_swag_bags ? "✅" : "❌"}`,
    `*Stage:* ${getShipmentStageLabel(shipment.stage)}`,
    `*Date shipped:* ${formatShipmentDate(shipment.date_shipped)}`,
    `*ETA:* ${formatShipmentDate(shipment.eta)}`,
    `*Total boxes:* ${shipment.total_boxes}`,
    `*Boxes received:* ${shipment.boxes_received}`,
    `*Arrived on:* ${formatShipmentDate(shipment.arrival_date)}`,
    `*Sponsor shipping notes:* ${shipment.sponsor_shipping_notes || "—"}`,
  ].join("\n");
};

export const getShipmentStageColor = (stage: ShipmentStage): LabelColor => {
  switch (stage) {
    case "draft":
      return "secondary";
    case "shipped":
      return "primary";
    case "partially_received":
      return "warning";
    case "received":
      // i would like to use a different color, such as success-light
      // however, north.js's label has really bad text contrast when using -light variants
      return "success";
    case "packed":
      return "success";
    default:
      return "secondary";
  }
};
